import { t } from '../i18n/index.js';

export type ConnectionQuality = 'excellent' | 'good' | 'fair' | 'poor' | 'offline';

/** Round-trip bands tuned for a 20 Hz authoritative race with prediction. */
export function connectionQuality(pingMs: number | null | undefined, connected = true): ConnectionQuality {
  if (!connected) return 'offline';
  if (pingMs === null || pingMs === undefined || !Number.isFinite(pingMs)) return 'good';
  if (pingMs <= 60) return 'excellent';
  if (pingMs <= 120) return 'good';
  if (pingMs <= 200) return 'fair';
  return 'poor';
}

const BARS: Record<ConnectionQuality, number> = { excellent: 4, good: 3, fair: 2, poor: 1, offline: 0 };
const LABEL: Record<ConnectionQuality, string> = {
  excellent: 'Excellent connection',
  good: 'Good connection',
  fair: 'Fair connection',
  poor: 'Poor connection',
  offline: 'Reconnecting',
};

/** Coarse display value: ping jitters by a few ms and must not re-render UI. */
export function displayPing(pingMs: number | null | undefined): number | null {
  if (pingMs === null || pingMs === undefined || !Number.isFinite(pingMs)) return null;
  const rounded = pingMs < 100 ? Math.round(pingMs / 5) * 5 : Math.round(pingMs / 10) * 10;
  return Math.max(1, rounded);
}

/** Four-bar signal meter; the bars are pure CSS (`.signal-bars`). */
export function signalBarsHtml(pingMs: number | null | undefined, connected = true, withLabel = true): string {
  const quality = connectionQuality(pingMs, connected);
  const ping = displayPing(pingMs);
  const lit = BARS[quality];
  const text = !connected ? t('LINK') : ping === null ? '' : `${ping}<small>MS</small>`;
  const title = `${t(LABEL[quality])}${connected && ping !== null ? ` · ${ping} ms` : ''}`;
  return `<span class="signal-bars" data-quality="${quality}" title="${title}" aria-label="${title}" role="img">`
    + `<span class="signal-bars-meter" aria-hidden="true">${[1, 2, 3, 4].map((bar) => `<i${bar <= lit ? ' class="lit"' : ''}></i>`).join('')}</span>`
    + `${withLabel && text ? `<span class="signal-bars-text" aria-hidden="true">${text}</span>` : ''}</span>`;
}
